/* eslint complexity: ["error", {max: 14}] -- registered/unregistered + sensitive/pre-treatment branching */
/* eslint max-lines-per-function: ["error", {max: 155}] -- registered/unregistered + sensitive/pre-treatment branching */
import { useEffect } from "react";

import isNil from "lodash/isNil";

import type { GroupClassSeriesAnalyticsSources } from "@/constants/analytics/Analytics";
import {
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { GroupRegistrationText } from "@/constants/Schedule";
import {
  ConfirmationAlertTestID,
  ExploreTestID,
  TextTestID,
} from "@/constants/TestID";
import { IllustrationSize } from "@/constants/UI";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import CoreTag from "@/ui/components/groupClasses/series/CoreTag";
import {
  PreTreatmentDetailsActionBar,
  SensitiveDetailsActionBar,
} from "@/ui/components/groupClasses/series/details/ActionBars";
import useGroupRegistrationFlow from "@/ui/components/groupClasses/series/details/useGroupRegistrationFlow";
import ConfirmGroupRegistrationBottomSheet from "@/ui/components/groupClasses/series/session/ConfirmGroupRegistrationBottomSheet";
import EditGroupRegistrationBottomSheet from "@/ui/components/groupClasses/series/session/EditGroupRegistrationBottomSheet";
import GroupClassSessionHosts from "@/ui/components/groupClasses/series/session/GroupClassSessionHosts";
import GroupClassSessionList from "@/ui/components/groupClasses/series/session/GroupClassSessionList";
import RegisteredSectionCard from "@/ui/components/groupClasses/series/session/RegisteredSectionCard";
import ConfirmationAlert from "@/ui/composed/alerts/ConfirmationAlert";
import { FAIconName, Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { ColorToken } from "@/ui/theme/tokens";
import {
  shouldHideSensitiveGroups as getShouldHideSensitiveGroups,
  toGroupSessions,
} from "@/utils/groupClassV2.util";
import { joinTestIDs } from "@/utils/string.util";

interface GroupDetailsV2Props {
  eventDateString?: string;
  group: GroupDetails;
  // the specific registered section the user tapped; scopes the registered view to that
  // section (a user can hold multiple sections of the same group). When omitted, falls back
  // to the first registered section (entry points without a section id — Explore/Search/
  // Recommended — only reach a not-yet-registered group, so the match is unambiguous).
  groupSectionId?: string;
  // selected user is pre-treatment (admissions): show the pre-treatment banner and hide
  // the edit affordance — the group appears on their schedule once treatment starts
  isUserPreTreatment?: boolean;
  onDeclineEventPress?: () => void;
  onRegistrationError?: () => void;
  patientId: string;
  // the surface the user entered from; forwarded to the registration flow's analytics so
  // register/unregister events stay attributable to their entry point
  source: GroupClassSeriesAnalyticsSources;
}

const GroupDetailsHeader = ({
  baseTestID,
  group,
}: {
  baseTestID: string;
  group: GroupDetails;
}) => (
  <VStack gap={12}>
    <HStack alignItems="center" justifyContent="space-between">
      <Box testID={joinTestIDs(baseTestID, ExploreTestID.Illustration)}>
        {group.iconName &&
          getIllustrationIcon({
            height: IllustrationSize.Modal,
            iconName: group.iconName,
            width: IllustrationSize.Modal,
          })}
      </Box>
      {group.isCore && (
        <Box>
          <CoreTag
            testID={joinTestIDs(
              baseTestID,
              ExploreTestID.GroupClassSeriesDetailsCoreTag,
            )}
          />
        </Box>
      )}
    </HStack>
    <Text testID={joinTestIDs(baseTestID, TextTestID.Title)} variant="h4">
      {group.title}
    </Text>
  </VStack>
);

const GroupDetailsV2 = ({
  eventDateString,
  group,
  groupSectionId,
  isUserPreTreatment,
  onDeclineEventPress,
  onRegistrationError,
  patientId,
  source,
}: GroupDetailsV2Props) => {
  const baseTestID = ExploreTestID.GroupClassSeriesDetails;
  const { trackPageView } = useAnalytics();
  const { externalId: appUserId, isSupport: isAppUserSupport } =
    useAppUserSelector();
  // support user viewing a linked patient (the selected user is not themselves)
  const isViewingLinkedPatient =
    isAppUserSupport &&
    getShouldHideSensitiveGroups({ appUserId, selectedUserId: patientId });
  // a proxy cannot see or register a linked patient's sensitive group — the patient must
  // register via their own account, so hide the session list (only the banner shows)
  const shouldHideSessions = isViewingLinkedPatient && group.isSensitive;

  const sessions = toGroupSessions(group);
  // when a specific section was tapped (Your Groups card / schedule event), scope strictly to
  // it. Otherwise (arriving from Explore/Search/Recommended, which carry no section id) fall
  // back to the first registered section so a just-registered section still shows its card —
  // those surfaces only lead here for a not-yet-registered group, so it's unambiguous.
  const registeredSection = isNil(groupSectionId)
    ? sessions.find((s) => s.isUserRegistered)
    : sessions.find((s) => s.isUserRegistered && s.id === groupSectionId);

  useEffect(() => {
    trackPageView(GroupClassSeriesAnalyticsPageView.GroupDetailsV2, {
      [AnalyticsFieldName.GroupClassId]: group.groupId,
      [AnalyticsFieldName.Title]: group.title,
    });
  }, [group.groupId, group.title, trackPageView]);

  const {
    closeEdit,
    closeUnregister,
    deselectSession,
    flushPendingToast,
    isEditOpen,
    isLoading,
    isUnregisterOpen,
    onConfirmRegister,
    onConfirmUnregister,
    openEdit,
    openUnregister,
    selectedSession,
    selectSession,
  } = useGroupRegistrationFlow({
    groupId: group.groupId,
    onError: onRegistrationError,
    patientId,
    registeredSection,
    sessions,
    source,
  });

  // proxy viewing a linked patient's sensitive group sees only the banner — no session
  // list / hosts. Otherwise show the registered section's hosts, or the selectable list.
  const renderSessions = () => {
    if (shouldHideSessions) return null;
    if (registeredSection) {
      return <GroupClassSessionHosts hosts={registeredSection.hosts} />;
    }
    return (
      <GroupClassSessionList
        description={GroupRegistrationText.AvailableSessionsDescription}
        onSelect={selectSession}
        sessions={sessions}
        title={GroupRegistrationText.AvailableSessionsTitle}
      />
    );
  };

  return (
    <VStack gap={24} paddingTop={24}>
      <GroupDetailsHeader baseTestID={baseTestID} group={group} />
      {/* when a proxy views a linked patient's sensitive group, hide the registered
          card (and its Edit action) too — only the banner should show */}
      {registeredSection && !shouldHideSessions && (
        <RegisteredSectionCard
          eventDateString={eventDateString}
          // event view leads with the tapped occurrence's date; group view keeps "Next on ..."
          nextDateString={
            isNil(eventDateString)
              ? registeredSection.nextDateString
              : undefined
          }
          onDeclineEventPress={onDeclineEventPress}
          // pre-treatment users can't edit — the group joins their schedule once treatment starts
          onEditPress={isUserPreTreatment ? undefined : openEdit}
          recurrenceString={registeredSection.title}
          sessionCount={registeredSection.sessionCount}
          timeRange={registeredSection.timeRange}
        />
      )}
      {group.description && (
        <Text
          color={ColorToken.NeutralSecondary}
          testID={joinTestIDs(baseTestID, TextTestID.Description)}
          variant="body"
        >
          {group.description}
        </Text>
      )}
      <SensitiveDetailsActionBar
        isAppUserSupport={isAppUserSupport}
        isSensitive={group.isSensitive}
        isViewingLinkedPatient={isViewingLinkedPatient}
      />
      {isUserPreTreatment && <PreTreatmentDetailsActionBar />}
      {renderSessions()}
      <EditGroupRegistrationBottomSheet
        isOpen={isEditOpen}
        onClose={closeEdit}
        onDismissAnimationEnd={flushPendingToast}
        onSelectSession={selectSession}
        onUnregisterPress={openUnregister}
        sessions={sessions}
      />
      <ConfirmGroupRegistrationBottomSheet
        hosts={selectedSession?.hosts}
        iconName={group.iconName}
        isOpen={Boolean(selectedSession)}
        isRegistering={isLoading}
        onClose={deselectSession}
        onConfirm={onConfirmRegister}
        onDismissAnimationEnd={flushPendingToast}
        recurrenceString={selectedSession?.title}
        sessionCount={selectedSession?.sessionCount}
        timeRange={selectedSession?.timeRange}
        title={group.title}
      />
      <ConfirmationAlert
        confirmationType="destructive"
        confirmLeftIcon={FAIconName.UserSlash}
        confirmText={GroupRegistrationText.UnregisterCta}
        isConfirmFullWidth
        isOpen={isUnregisterOpen}
        message={GroupRegistrationText.UnregisterConfirmMessage}
        onConfirm={onConfirmUnregister}
        onDismiss={closeUnregister}
        testID={ConfirmationAlertTestID.UnregisterGroup}
        title={GroupRegistrationText.UnregisterConfirmTitle}
      />
    </VStack>
  );
};

export default GroupDetailsV2;
